import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaEye } from 'react-icons/fa';
import DashboardLayout from './DashboardLayout';
import { ApiLoadState, ApiLoadTableRow } from './ApiLoadState';
import TablePagination from './TablePagination';
import { useGetEmployeeByIdQuery } from '../../redux/employeeApi';
import {
  useCreateLeaveApplyMutation,
  useCreateOutsourceLeaveRequestMutation,
  useGetLeaveAppliesQuery,
  useGetOutsourceLeaveRequestsQuery,
  useUpdateLeaveApplyStatusMutation,
  useUpdateOutsourceLeaveRequestMutation,
  useUpdateOutsourceLeaveStatusMutation,
} from '../../redux/leaveApi';
import { useAuth } from '../../context/AuthContext';
import { accountImageDisplayUrl } from '../../utils/accountImageUrl';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { DEFAULT_PAGE_SIZE, getPaginationMeta, getServerPagination } from '../../utils/pagination';
import { useGetAccountBehaviourTrackingsQuery } from '../../redux/behaviourApi';
import {
  computeTrackingComplianceSummary,
  extractPerformance,
  extractTrackingList,
  formatComplianceStatus,
  formatPercent,
  getNonComplianceCount,
} from '../../utils/behaviourTracking';
import BehaviourPerformanceModal from './BehaviourPerformanceModal';
import { parseApiLeaveSummary, formatDayTypeLabel } from '../../utils/leaveSummary';
import { hasCanLeaveAccess, leaveApplyStatusForRole } from '../../utils/leaveAccess';
import {
  DEFAULT_LEAVE_LIST_STATUS_FILTER,
  LEAVE_STATUS_FILTER_OPTIONS,
} from '../../utils/leaveStatus';
import { getProbationFromAccount } from '../../utils/accountProbation';
import {
  departmentNameFromAccount,
  locationNamesFromAccount,
} from '../../utils/accountRoleClassification';
import { employeeListBackPath } from '../../utils/employeeListState';
import { isReportingSupervisorValue } from '../../utils/behaviourAccess';
import LeaveBalanceSummary from './LeaveBalanceSummary';
import ProbationInfoFields from './ProbationInfoFields';
import LeaveStatusBadge from './LeaveStatusBadge';
import { FilterToggleButton } from './FilterToggleButton';

const LEAVE_TYPE_OPTIONS = [
  { value: 'EL', label: 'EL - Earned Leave' },
  { value: 'SL', label: 'SL - Sick Leave' },
  { value: 'CL', label: 'CL - Casual Leave' },
  { value: 'LOP', label: 'LOP - Loss Of Pay' },
];

const OUTSOURCE_LEAVE_TYPE_OPTIONS = [
  { value: 'LOP', label: 'LOP - Loss Of Pay' },
];

const DAY_TYPE_OPTIONS = [
  { value: 'full_day', label: 'Full Day' },
  { value: 'first_half', label: 'First Half' },
  { value: 'second_half', label: 'Second Half' },
];

const LEAVE_DURATION_OPTIONS = [
  { value: 'single', label: 'Single Day' },
  { value: 'multiple', label: 'Multiple Days' },
];
const formInputClasses =
  'mt-1 w-full rounded-lg border border-gray-200 bg-[#faf8f5] px-3 py-2 text-sm text-gray-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';
const formReadonlyClasses =
  'mt-1 w-full rounded-lg border border-gray-200 bg-[#faf8f5] px-3 py-2 text-sm text-gray-900';

function isPdfUrl(url) {
  if (!url) return false;
  const noQuery = String(url).split('?')[0];
  const last = noQuery.split('/').filter(Boolean).pop() || '';
  try {
    return decodeURIComponent(last).toLowerCase().endsWith('.pdf');
  } catch {
    return last.toLowerCase().endsWith('.pdf');
  }
}

function fileNameFromUrl(url, fallback = 'document') {
  if (!url) return fallback;
  const noQuery = String(url).split('?')[0];
  const last = noQuery.split('/').filter(Boolean).pop() || fallback;
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
}
function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function currentMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    start: formatLocalDate(start),
    end: formatLocalDate(end),
  };
}

function currentFyRange() {
  const now = new Date();
  const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return {
    start: new Date(year, 3, 1),
    end: new Date(year + 1, 2, 31, 23, 59, 59),
  };
}

function parseLeaveDate(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
    const [day, month, year] = raw.split('/').map(Number);
    const parsed = new Date(year, month - 1, day);
    if (
      !Number.isNaN(parsed.getTime()) &&
      parsed.getFullYear() === year &&
      parsed.getMonth() === month - 1 &&
      parsed.getDate() === day
    ) {
      return parsed;
    }
    return null;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const parsed = new Date(`${raw}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatAppliedOn(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  const day = String(parsed.getDate()).padStart(2, '0');
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const year = parsed.getFullYear();
  return `${day}/${month}/${year}`;
}

function formatLeaveDay(value) {
  if (!value) return '—';
  const parsed = parseLeaveDate(value) || new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatHistoryActionLabel(actionType) {
  const raw = String(actionType || '').trim();
  if (!raw) return 'Update';
  return raw
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatHistoryActor(actionBy) {
  if (!actionBy || typeof actionBy !== 'object') return '';
  const name = String(actionBy.name || '').trim();
  const empId = String(actionBy.employee_id || actionBy.emp_id || '').trim();
  if (name && empId) return `${name} (${empId})`;
  return name || empId || '';
}

function formatHistoryEndDateLine(oldEnd, newEnd) {
  const from = formatLeaveDay(oldEnd);
  const to = formatLeaveDay(newEnd);
  if (from === '—' && to === '—') return '';
  if (from === to) return `End date: ${to}`;
  return `End date: ${from} → ${to}`;
}

function firstBehaviourText(...values) {
  for (const value of values) {
    if (value == null || value === '') continue;
    if (typeof value === 'object') {
      const nested = value.name ?? value.title ?? value.label ?? '';
      if (nested) return String(nested);
      continue;
    }
    return String(value);
  }
  return '';
}

function extractBehaviourItems(row) {
  if (!row || typeof row !== 'object') return [];
  if (Array.isArray(row.behaviours)) return row.behaviours;
  if (Array.isArray(row.behaviour_details)) return row.behaviour_details;
  if (Array.isArray(row.account_behaviour_trackings)) return row.account_behaviour_trackings;
  if (Array.isArray(row.details)) return row.details;
  return [];
}

function formatLevelType(value) {
  const compact = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
  const labels = {
    level1: 'Level 1',
    level2: 'Level 2',
    level3: 'Level 3',
    level4: 'Level 4',
  };
  return labels[compact] || value || '—';
}

function formatEmploymentNature(value, fallbackOutsource) {
  const compact = String(value || '').trim().toLowerCase();
  if (compact === 'gspl') return 'GSPL';
  if (compact === 'vendor') return 'Vendor';
  if (fallbackOutsource === true) return 'Vendor';
  if (fallbackOutsource === false) return 'GSPL';
  return value || '—';
}

function formatCostCentreType(value, fallbackProduction) {
  const compact = String(value || '').trim().toLowerCase();
  if (compact === 'restaurant') return 'Restaurant Operations';
  if (compact === 'manufacturing') return 'Manufacturing';
  if (compact === 'corporate') return 'Corporate';
  if (fallbackProduction === true) return 'Manufacturing';
  return value || '—';
}

function formatTrackingDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${d.getFullYear()}`;
}

function formatReviewMonth(value, trackingDate) {
  if (value) return String(value);
  if (!trackingDate) return '—';
  const d = new Date(trackingDate);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
}

function mapEmployeeBehaviourRow(row, index) {
  const trackingDate = firstBehaviourText(row?.tracking_date, row?.date, row?.created_at);
  const behaviours = extractBehaviourItems(row);
  const summary = computeTrackingComplianceSummary(behaviours);
  return {
    __key: row?.id ?? row?.tracking_id ?? `row-${index}`,
    accountId: row?.account_id ?? row?.account?.id ?? row?.employee?.id ?? '',
    trackingDate,
    trackingDateLabel: formatTrackingDate(trackingDate),
    reviewMonth: formatReviewMonth(
      firstBehaviourText(row?.review_month, row?.month),
      trackingDate
    ),
    reportingManager: firstBehaviourText(
      row?.reporting_supervisor_name,
      row?.reporting_manager_name,
      row?.reporting_manager,
      row?.manager_name
    ),
    reviewedBy: firstBehaviourText(row?.reviewed_by_name, row?.reviewed_by),
    behaviours,
    monthlyPerformance: firstBehaviourText(
      row?.monthly_performance,
      row?.monthlyPerformance,
      summary.complianceRate
    ),
    yearlyPerformance: firstBehaviourText(row?.yearly_performance, row?.yearlyPerformance),
    complianceSummary: summary,
  };
}

function behaviourRowInDateRange(row, from, to) {
  if (!from && !to) return true;
  const d = row?.trackingDate ? new Date(row.trackingDate) : null;
  if (!d || Number.isNaN(d.getTime())) return false;
  if (from) {
    const start = new Date(from);
    if (!Number.isNaN(start.getTime())) {
      start.setHours(0, 0, 0, 0);
      if (d < start) return false;
    }
  }
  if (to) {
    const end = new Date(to);
    if (!Number.isNaN(end.getTime())) {
      end.setHours(23, 59, 59, 999);
      if (d > end) return false;
    }
  }
  return true;
}

function personDisplayName(person, fallback = '') {
  if (person == null || person === '') return fallback;
  if (typeof person === 'string' || typeof person === 'number') {
    return String(person).trim() || fallback;
  }
  if (typeof person !== 'object') return fallback;
  const name = String(person.name || person.employee_name || '').trim();
  const code = String(person.employee_id || person.emp_id || '').trim();
  if (name && code) return `${name} (${code})`;
  return name || code || fallback;
}

function getLeaveDateLabel(leave) {
  const leaveStart = leave?.start_date || '-';
  const leaveEnd = leave?.end_date || leave?.start_date || '-';
  if (leaveStart !== '-' && leaveEnd !== '-' && leaveStart !== leaveEnd) {
    return `${leaveStart} → ${leaveEnd}`;
  }
  return leaveStart;
}

function AdminEmployeeDetailsPage({ viewType = 'employee' }) {
  const { id } = useParams();
  const location = useLocation();
  const employeeId = Number(id);
  const isOutsourceView = viewType === 'outsource';
  const employeesListPath = employeeListBackPath(location, { isOutsource: isOutsourceView });
  const leaveTypeOptions = isOutsourceView ? OUTSOURCE_LEAVE_TYPE_OPTIONS : LEAVE_TYPE_OPTIONS;
  const { user, roleType, canBehaviourTrack } = useAuth();
  // Apply / Accept / Reject — based on logged-in user only
  const canApplyLeave = hasCanLeaveAccess(roleType);
  const showBehaviourTab = !isOutsourceView && Boolean(canBehaviourTrack);
  const showLeaveFeatures = isOutsourceView || canApplyLeave;
  const canApplyLeaveHere = isOutsourceView || canApplyLeave;
  const showTabBar = isOutsourceView || canApplyLeave || showBehaviourTab;

  const defaultRange = useMemo(() => currentMonthRange(), []);
  const fyRange = useMemo(() => currentFyRange(), []);

  const { data: employeeRes, isFetching, isLoading, error: employeeError, refetch: refetchEmployee } = useGetEmployeeByIdQuery(employeeId, {
    skip: Number.isNaN(employeeId),
  });

  const employee = useMemo(() => {
    const data = employeeRes?.data || employeeRes || null;
    if (!data || typeof data !== 'object') return null;
    return {
      id: data.id,
      empId: data.employee_id || '-',
      name: data.name || '-',
      email: data.email || '-',
      contact: data.contact || '-',
      department: departmentNameFromAccount(data) || '-',
      designation:
        data.role_name ||
        data.designation_name ||
        (typeof data.designation === 'object' ? data.designation?.name : data.designation) ||
        '-',
      location: locationNamesFromAccount(data) || '-',
      manager: data.reporting_supervisor_name || '-',
      isReportingSupervisor: isReportingSupervisorValue(
        data.is_reporting_supervisor ?? data.isReportingSupervisor
      )
        ? 'Yes'
        : 'No',
      isOutsource: formatEmploymentNature(
        data.employment_nature || data.employmentNature,
        data.is_outsource ?? data.isOutsource
      ),
      isProduction: formatCostCentreType(
        data.cost_centre_type || data.costCentreType,
        data.is_production ?? data.isProduction
      ),
      levelType: formatLevelType(data.level_type || data.levelType),
      dob: data.dob || '-',
      doj: data.date_of_joining || '-',
      createdAt: formatAppliedOn(data.created_at ?? data.createdAt),
      aadharNo: data.aadhar_no || '-',
      panNo: data.pan_number || '-',
      roleType: data.role_type ?? '',
      employeeImage: accountImageDisplayUrl(data, 'employee_image') || '',
      aadharFrontImage: accountImageDisplayUrl(data, 'aadhar_front_image') || '',
      panImage: accountImageDisplayUrl(data, 'pan_image') || '',
      ...getProbationFromAccount(data),
    };
  }, [employeeRes]);

  const [activeTab, setActiveTab] = useState('details');
  const [zoomImage, setZoomImage] = useState(null);
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [extendLeave, setExtendLeave] = useState(null);
  const [extendForm, setExtendForm] = useState({ end_date: '', comment: '' });
  const [statusAction, setStatusAction] = useState(null);
  const [statusComment, setStatusComment] = useState('');
  const [viewLeave, setViewLeave] = useState(null);
  const [isLeaveHistoryOpen, setIsLeaveHistoryOpen] = useState(false);
  const [viewBehaviour, setViewBehaviour] = useState(null);
  const [isBehaviourPerformanceOpen, setIsBehaviourPerformanceOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isBehaviourFilterOpen, setIsBehaviourFilterOpen] = useState(false);
  const [behaviourSearch, setBehaviourSearch] = useState('');
  const [behaviourDateDraft, setBehaviourDateDraft] = useState({
    from: defaultRange.start,
    to: defaultRange.end,
  });
  const [behaviourDateFilter, setBehaviourDateFilter] = useState({
    from: defaultRange.start,
    to: defaultRange.end,
  });
  const [page, setPage] = useState(1);
  const [behaviourPage, setBehaviourPage] = useState(1);
  const [statusDraft, setStatusDraft] = useState(DEFAULT_LEAVE_LIST_STATUS_FILTER);
  const [statusFilter, setStatusFilter] = useState(DEFAULT_LEAVE_LIST_STATUS_FILTER);
  const [dateDraft, setDateDraft] = useState({ from: defaultRange.start, to: defaultRange.end });
  const [dateFilter, setDateFilter] = useState({ from: defaultRange.start, to: defaultRange.end });
  const pageSize = DEFAULT_PAGE_SIZE;
  const leaveQuery = useMemo(() => {
    const params = {
      account_id: employeeId,
      page,
      per_page: pageSize,
    };
    if (!isOutsourceView) {
      if (dateFilter.from) params.start_date = dateFilter.from;
      if (dateFilter.to) params.end_date = dateFilter.to;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
    }
    return params;
  }, [
    employeeId,
    page,
    pageSize,
    dateFilter.from,
    dateFilter.to,
    statusFilter,
    isOutsourceView,
  ]);

  const employeeLeaveQuery = useGetLeaveAppliesQuery(leaveQuery, {
    skip:
      isOutsourceView ||
      !showLeaveFeatures ||
      Number.isNaN(employeeId) ||
      activeTab !== 'leave',
    refetchOnMountOrArgChange: true,
  });
  const outsourceLeaveQuery = useGetOutsourceLeaveRequestsQuery(
    {
      account_id: employeeId,
      page,
      per_page: pageSize,
    },
    {
      skip:
        !isOutsourceView ||
        !showLeaveFeatures ||
        Number.isNaN(employeeId) ||
        activeTab !== 'leave',
      refetchOnMountOrArgChange: true,
    }
  );
  const {
    data: leavesRes,
    isFetching: isFetchingLeaves,
    isLoading: isLoadingLeaves,
    error: leavesError,
    refetch: refetchLeaves,
  } = isOutsourceView ? outsourceLeaveQuery : employeeLeaveQuery;
  const [createLeaveApply, { isLoading: isCreatingEmployeeLeave }] =
    useCreateLeaveApplyMutation();
  const [createOutsourceLeaveRequest, { isLoading: isCreatingOutsourceLeave }] =
    useCreateOutsourceLeaveRequestMutation();
  const isCreatingLeave = isCreatingEmployeeLeave || isCreatingOutsourceLeave;
  const [updateLeaveApplyStatus, { isLoading: isUpdatingLeaveStatus }] =
    useUpdateLeaveApplyStatusMutation();
  const [updateOutsourceLeaveRequest, { isLoading: isUpdatingOutsourceLeave }] =
    useUpdateOutsourceLeaveRequestMutation();
  const [updateOutsourceLeaveStatus, { isLoading: isUpdatingOutsourceStatus }] =
    useUpdateOutsourceLeaveStatusMutation();

  const behaviourQuery = useMemo(() => {
    const params = {
      accountId: employeeId,
      page: behaviourPage,
      per_page: pageSize,
    };
    const q = behaviourSearch.trim();
    if (q) params.search = q;
    if (behaviourDateFilter.from) params.start_date = behaviourDateFilter.from;
    if (behaviourDateFilter.to) params.end_date = behaviourDateFilter.to;
    return params;
  }, [
    employeeId,
    behaviourPage,
    pageSize,
    behaviourSearch,
    behaviourDateFilter.from,
    behaviourDateFilter.to,
  ]);
  const {
    data: behaviourRes,
    isFetching: isFetchingBehaviour,
    isLoading: isLoadingBehaviour,
    error: behaviourError,
    refetch: refetchBehaviour,
  } = useGetAccountBehaviourTrackingsQuery(behaviourQuery, {
    skip: !showBehaviourTab || Number.isNaN(employeeId) || activeTab !== 'behaviour',
  });

  useEffect(() => {
    setActiveTab('details');
    setPage(1);
    setBehaviourPage(1);
    setViewBehaviour(null);
    setBehaviourSearch('');
    setIsBehaviourFilterOpen(false);
    setBehaviourDateDraft({ from: '', to: '' });
    setBehaviourDateFilter({ from: '', to: '' });
    setStatusDraft(DEFAULT_LEAVE_LIST_STATUS_FILTER);
    setStatusFilter(DEFAULT_LEAVE_LIST_STATUS_FILTER);
    setDateDraft({ from: defaultRange.start, to: defaultRange.end });
    setDateFilter({ from: defaultRange.start, to: defaultRange.end });
  }, [employeeId, defaultRange.start, defaultRange.end]);

  useEffect(() => {
    if (!showLeaveFeatures && activeTab === 'leave') {
      setActiveTab('details');
    }
    if (!showBehaviourTab && activeTab === 'behaviour') {
      setActiveTab('details');
    }
  }, [showLeaveFeatures, showBehaviourTab, activeTab]);

  useEffect(() => {
    if (activeTab !== 'leave' || !showLeaveFeatures || Number.isNaN(employeeId)) return;
    refetchLeaves();
  }, [activeTab, showLeaveFeatures, employeeId, refetchLeaves]);

  const [leaveForm, setLeaveForm] = useState({
    leave_duration: 'single',
    start_date: '',
    end_date: '',
    leave_type: viewType === 'outsource' ? 'LOP' : LEAVE_TYPE_OPTIONS[2].value,
    day_type: DAY_TYPE_OPTIONS[0].value,
    description: '',
  });

  useEffect(() => {
    if (!isOutsourceView) return;
    setLeaveForm((prev) =>
      prev.leave_type === 'LOP' ? prev : { ...prev, leave_type: 'LOP' }
    );
  }, [isOutsourceView]);

  const applyBy = useMemo(() => {
    const name = user?.name || '';
    const empCode = user?.employeeCode || '';
    return `${name}${empCode ? ` (${empCode})` : ''}`.trim();
  }, [user]);

  const leaveRows = useMemo(() => {
    const list = leavesRes?.data || leavesRes || [];
    if (!Array.isArray(list) || !employee) return [];
    return [...list]
      .filter((item) => {
        if (item?.account_id == null || item?.account_id === '') return true;
        return Number(item.account_id) === Number(employee.id);
      })
      .map((item) => ({
        ...item,
        description: item.description || item.comment || '',
        apply_by: personDisplayName(
          item.applied_by,
          personDisplayName(item.apply_by, item.applied_by_name || '')
        ),
      }))
      .sort(
        (a, b) =>
          (parseLeaveDate(b?.start_date)?.getTime() || 0) -
          (parseLeaveDate(a?.start_date)?.getTime() || 0)
      );
  }, [leavesRes, employee]);

  const { currentPage, totalCount, pageSize: effectivePageSize } = getServerPagination(
    leavesRes,
    { page, pageSize }
  );
  const pageItems = leaveRows;

  const leaveSummary = useMemo(() => {
    const list = leavesRes?.data || leavesRes || [];
    const apiSummary = leavesRes?.leave_summary;
    if (!Array.isArray(list) || !employee) {
      return {
        total: 0,
        used: 0,
        remaining: 0,
        earnedLeave: { total: 0, used: 0, remaining: 0 },
        sickLeave: { total: 0, used: 0, remaining: 0 },
        casualLeave: { total: 0, used: 0, remaining: 0 },
        lopLeave: { total: 0, used: 0, remaining: 0 },
        applied: 0,
        accepted: 0,
        rejected: 0,
        lopApplied: 0,
        available: 0,
      };
    }

    const fyLeaves = list.filter((item) => {
      if (Number(item?.account_id) !== Number(employee.id)) return false;
      const date = parseLeaveDate(item?.start_date);
      return date && date >= fyRange.start && date <= fyRange.end;
    });

    const applied = fyLeaves.length;
    const accepted = fyLeaves.filter((l) => /approved|accepted/i.test(String(l?.status || ''))).length;
    const rejected = fyLeaves.filter((l) => /rejected/i.test(String(l?.status || ''))).length;
    const lopApplied = fyLeaves.filter((l) => /lop/i.test(String(l?.leave_type || ''))).length;

    const balance = parseApiLeaveSummary(apiSummary, {
      today: new Date(),
      lopUsedFallback: lopApplied,
    });

    return {
      ...balance,
      applied,
      accepted,
      rejected,
      lopApplied,
      available: balance.remaining,
    };
  }, [leavesRes, employee, fyRange]);

  const behaviourRows = useMemo(
    () =>
      extractTrackingList(behaviourRes)
        .map((row, index) => mapEmployeeBehaviourRow(row, index))
        .filter((row) => {
          if (!Number.isFinite(employeeId)) return true;
          if (row.accountId == null || row.accountId === '') return true;
          return Number(row.accountId) === Number(employeeId);
        }),
    [behaviourRes, employeeId]
  );
  const behaviourPerformance = useMemo(
    () => extractPerformance(behaviourRes),
    [behaviourRes]
  );
  const filteredBehaviourRows = useMemo(() => {
    const q = behaviourSearch.trim().toLowerCase();
    return behaviourRows.filter((row) => {
      if (!behaviourRowInDateRange(row, behaviourDateFilter.from, behaviourDateFilter.to)) {
        return false;
      }
      if (!q) return true;
      return [row.trackingDateLabel, row.reviewMonth, row.reportingManager]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [behaviourRows, behaviourSearch, behaviourDateFilter.from, behaviourDateFilter.to]);
  const behaviourServerPagination = getServerPagination(behaviourRes, {
    page: behaviourPage,
    pageSize,
  });
  const hasBehaviourServerPagination =
    Number(
      behaviourRes?.pagination?.total_count ?? behaviourRes?.data?.pagination?.total_count
    ) > 0;
  const behaviourClientMeta = getPaginationMeta(
    filteredBehaviourRows.length,
    behaviourPage,
    pageSize
  );
  const behaviourCurrentPage = hasBehaviourServerPagination
    ? behaviourServerPagination.currentPage
    : behaviourClientMeta.currentPage;
  const behaviourTotalCount = hasBehaviourServerPagination
    ? behaviourServerPagination.totalCount
    : filteredBehaviourRows.length;
  const behaviourPageItems = hasBehaviourServerPagination
    ? filteredBehaviourRows
    : filteredBehaviourRows.slice(
        (behaviourCurrentPage - 1) * pageSize,
        behaviourCurrentPage * pageSize
      );
  const hasBehaviourDateFilter =
    Boolean(behaviourDateFilter.from || behaviourDateFilter.to) &&
    (behaviourDateFilter.from !== defaultRange.start ||
      behaviourDateFilter.to !== defaultRange.end);
  const hasActiveLeaveFilters = Boolean(
    (statusFilter && statusFilter !== DEFAULT_LEAVE_LIST_STATUS_FILTER) ||
      dateFilter.from !== defaultRange.start ||
      dateFilter.to !== defaultRange.end
  );
  const handleLeaveChange = (field) => (e) => {
    const value = e?.target?.value ?? '';
    setLeaveForm((prev) => {
      if (field === 'leave_duration') {
        return {
          ...prev,
          leave_duration: value,
          start_date: '',
          end_date: '',
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const resetLeaveForm = () => {
    setLeaveForm((prev) => ({
      ...prev,
      leave_duration: 'single',
      start_date: '',
      end_date: '',
      description: '',
    }));
  };

  const handleCreateLeave = async (e) => {
    e.preventDefault();
    if (!employee) return;
    if (!isOutsourceView && !leaveForm.leave_type) return toast.error('Please select leave type');
    if (!leaveForm.day_type) return toast.error('Please select day type');
    if (!leaveForm.description.trim()) return toast.error('Please enter leave description');

    const isMultiple = leaveForm.leave_duration === 'multiple';
    let startDate = '';
    let endDate = '';

    if (isMultiple) {
      if (!leaveForm.start_date || !leaveForm.end_date) {
        return toast.error('Please select start and end dates');
      }
      if (new Date(leaveForm.start_date) > new Date(leaveForm.end_date)) {
        return toast.error('Invalid date range selected');
      }
      startDate = leaveForm.start_date;
      endDate = leaveForm.end_date;
    } else {
      if (!leaveForm.start_date) return toast.error('Please select leave date');
      startDate = leaveForm.start_date;
      endDate = leaveForm.start_date;
    }

    try {
      const applyStatus = leaveApplyStatusForRole(roleType);
      if (isOutsourceView) {
        const appliedById = Number(user?.id);
        if (!Number.isFinite(appliedById) || appliedById <= 0) {
          toast.error('Unable to identify applicant. Please login again.');
          return;
        }
        await createOutsourceLeaveRequest({
          account_id: employee.id,
          applied_by_id: appliedById,
          start_date: startDate,
          end_date: endDate,
          leave_type: 'LOP',
          day_type: leaveForm.day_type,
          comment: leaveForm.description.trim(),
          is_extended: false,
          status: applyStatus,
        }).unwrap();
      } else {
        await createLeaveApply({
          start_date: startDate,
          end_date: endDate,
          leave_type: leaveForm.leave_type,
          day_type: leaveForm.day_type,
          description: leaveForm.description.trim(),
          account_id: employee.id,
          apply_by: applyBy,
          status: applyStatus,
        }).unwrap();
      }
      toast.success(
        applyStatus === 'approved'
          ? 'Leave applied and approved successfully.'
          : 'Leave applied successfully.'
      );
      resetLeaveForm();
      setIsApplyModalOpen(false);
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to apply leave.');
    }
  };

  const openExtendLeave = (leave) => {
    setExtendLeave(leave);
    setExtendForm({
      end_date: leave?.end_date || leave?.start_date || '',
      comment: leave?.comment || leave?.description || '',
    });
  };

  const handleExtendLeave = async (e) => {
    e.preventDefault();
    if (!extendLeave?.id) return;
    if (!extendForm.end_date) return toast.error('Please select end date');
    if (!String(extendForm.comment || '').trim()) {
      return toast.error('Please enter comment');
    }
    const startDate = extendLeave.start_date || extendLeave.end_date;
    if (startDate && new Date(extendForm.end_date) < new Date(startDate)) {
      return toast.error('End date cannot be before start date');
    }
    try {
      await updateOutsourceLeaveRequest({
        id: extendLeave.id,
        end_date: extendForm.end_date,
        comment: String(extendForm.comment).trim(),
        is_extended: true,
        status: extendLeave.status || 'approved',
      }).unwrap();
      toast.success('Leave updated successfully.');
      setExtendLeave(null);
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to update leave.');
    }
  };

  const openOutsourceStatusAction = (leave, status) => {
    setStatusAction({ leave, status });
    setStatusComment('');
  };

  const handleOutsourceLeaveStatus = async (e) => {
    e.preventDefault();
    if (!statusAction?.leave?.id || !statusAction.status) return;
    const comment = String(statusComment || '').trim();
    if (!comment) return toast.error('Please enter comment');
    const actionById = Number(user?.id);
    if (!Number.isFinite(actionById) || actionById <= 0) {
      toast.error('Unable to identify action user. Please login again.');
      return;
    }
    try {
      await updateOutsourceLeaveStatus({
        id: statusAction.leave.id,
        status: statusAction.status,
        action_by_id: actionById,
        comment,
      }).unwrap();
      toast.success(`Leave ${statusAction.status} successfully.`);
      setStatusAction(null);
      setStatusComment('');
    } catch (err) {
      toast.error(
        err?.data?.message || `Failed to ${statusAction.status} leave.`
      );
    }
  };

  const handleLeaveStatus = async (leaveId, status) => {
    try {
      await updateLeaveApplyStatus({ id: leaveId, status }).unwrap();
      toast.success(`Leave ${status.toLowerCase()} successfully.`);
    } catch (err) {
      toast.error(err?.data?.message || `Failed to ${status.toLowerCase()} leave.`);
    }
  };

  const renderLeaveActions = (leave, { isFinal }, buttonWrapClass = 'inline-flex items-center gap-2') => {
    const status = String(leave?.status || 'pending');
    const isApprovedLike = /approved|accepted|extended/i.test(status);

    return (
    <div className={buttonWrapClass}>
      <button
        type="button"
        onClick={() => {
          setIsLeaveHistoryOpen(false);
          setViewLeave(leave);
        }}
        className="inline-flex shrink-0 items-center justify-center rounded-md p-1 text-red-700 hover:bg-amber-50/60 hover:text-red-800"
        aria-label="View leave"
        title="View"
      >
        <FaEye className="h-5 w-5" />
      </button>
      {isOutsourceView ? (
        <>
          {!isFinal ? (
            <>
              <button
                type="button"
                disabled={isUpdatingOutsourceStatus}
                onClick={() => openOutsourceStatusAction(leave, 'approved')}
                className="shrink-0 rounded-md bg-green-600 px-2 py-1 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Accept
              </button>
              <button
                type="button"
                disabled={isUpdatingOutsourceStatus}
                onClick={() => openOutsourceStatusAction(leave, 'rejected')}
                className="shrink-0 rounded-md bg-red-600 px-2 py-1 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Reject
              </button>
              <button
                type="button"
                disabled={isUpdatingOutsourceStatus}
                onClick={() => openOutsourceStatusAction(leave, 'cancelled')}
                className="shrink-0 rounded-md bg-gray-600 px-2 py-1 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => openExtendLeave(leave)}
                className="shrink-0 rounded-md bg-brand px-2 py-1 text-xs font-semibold text-white"
              >
                Extend
              </button>
            </>
          ) : isApprovedLike ? (
            <>
              <button
                type="button"
                disabled={isUpdatingOutsourceStatus}
                onClick={() => openOutsourceStatusAction(leave, 'cancelled')}
                className="shrink-0 rounded-md bg-gray-600 px-2 py-1 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => openExtendLeave(leave)}
                className="shrink-0 rounded-md bg-brand px-2 py-1 text-xs font-semibold text-white"
              >
                Extend
              </button>
            </>
          ) : null}
        </>
      ) : canApplyLeave && !isFinal ? (
        <>
          <button
            type="button"
            disabled={isUpdatingLeaveStatus}
            onClick={() => handleLeaveStatus(leave.id, 'approved')}
            className="shrink-0 rounded-md bg-green-600 px-2 py-1 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            Accept
          </button>
          <button
            type="button"
            disabled={isUpdatingLeaveStatus}
            onClick={() => handleLeaveStatus(leave.id, 'rejected')}
            className="shrink-0 rounded-md bg-red-600 px-2 py-1 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            Reject
          </button>
        </>
      ) : null}
    </div>
    );
  };

  const hasLeaveData = (leavesRes?.data || leavesRes || []).length > 0;
  const leaveListReady = hasLeaveData || (!isLoadingLeaves && !isFetchingLeaves);

  return (
    <DashboardLayout
      title={isOutsourceView ? 'Helper Employee Information' : 'Employee Information'}
    >
      <div className="space-y-4 py-4">
        <div className="flex items-center justify-end">
          <span className="text-sm text-gray-500">
            <Link to="/dashboard/admin" className="hover:text-brand">
              Home
            </Link>
            {' / '}
            <Link
              to={employeesListPath}
              className="hover:text-brand"
            >
              {isOutsourceView ? 'Helper Employees' : 'Employees'}
            </Link>
            {' / '}
            <span className="text-brand">
              {isOutsourceView ? 'Helper Employee Details' : 'Employee Details'}
            </span>
          </span>
        </div>
        <div className="rounded-2xl bg-white shadow-sm">
          <div className="p-4 sm:p-6">
            <ApiLoadState
              isLoading={isLoading}
              isFetching={isFetching}
              hasData={Boolean(employee)}
              error={employeeError}
              refetch={refetchEmployee}
              message="Loading employee details..."
            />
            {!employeeError && !isLoading && !isFetching && !employee ? (
              <p className="py-8 text-center text-sm text-gray-500">Employee details not found.</p>
            ) : !employeeError && employee ? (
              <>
                <div className="rounded-xl border border-gray-100 bg-[#faf8f5] p-4 sm:p-5">
                  <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center">
                    <button
                      type="button"
                      className="h-28 w-28 shrink-0 overflow-hidden rounded-full bg-gray-100 ring-2 ring-[#F7E5D5] sm:h-32 sm:w-32"
                      onClick={() => {
                        const src =
                          resolveAssetUrl(employee.employeeImage) || employee.employeeImage;
                        if (!src || isPdfUrl(src)) return;
                        setZoomImage({
                          src,
                          title: employee.name,
                        });
                      }}
                      aria-label="Preview employee photo"
                    >
                      {employee.employeeImage &&
                      !isPdfUrl(
                        resolveAssetUrl(employee.employeeImage) || employee.employeeImage
                      ) ? (
                        <img
                          src={resolveAssetUrl(employee.employeeImage) || employee.employeeImage}
                          alt={employee.name || 'Employee'}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                          No photo
                        </div>
                      )}
                    </button>
                    <div className="text-center sm:text-left">
                      <p className="text-xl font-semibold text-gray-900">{employee.name}</p>
                      <div className="mt-2 flex flex-wrap justify-center gap-3 sm:justify-start">
                        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                          <p className="text-xs text-gray-500">Employee ID</p>
                          <p className="text-sm font-semibold text-gray-900">{employee.empId}</p>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                          <p className="text-xs text-gray-500">Designation</p>
                          <p className="text-sm font-semibold text-gray-900">{employee.designation}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {showTabBar ? (
                  <div className="mt-5 inline-flex flex-wrap rounded-lg border border-gray-200 bg-[#faf8f5] p-1">
                    <button
                      type="button"
                      onClick={() => setActiveTab('details')}
                      className={`rounded-md px-3 py-1.5 text-sm font-medium transition sm:px-4 sm:py-2 ${
                        activeTab === 'details' ? 'bg-brand text-white shadow-sm' : 'text-gray-700 hover:bg-white'
                      }`}
                    >
                      {isOutsourceView
                        ? 'Helper Employee Information'
                        : 'Employee Information'}
                    </button>
                    {showLeaveFeatures ? (
                      <button
                        type="button"
                        onClick={() => setActiveTab('leave')}
                        className={`rounded-md px-3 py-1.5 text-sm font-medium transition sm:px-4 sm:py-2 ${
                          activeTab === 'leave' ? 'bg-brand text-white shadow-sm' : 'text-gray-700 hover:bg-white'
                        }`}
                      >
                        Leave History
                      </button>
                    ) : null}
                    {showBehaviourTab ? (
                      <button
                        type="button"
                        onClick={() => {
                          setActiveTab('behaviour');
                          setBehaviourPage(1);
                        }}
                        className={`rounded-md px-3 py-1.5 text-sm font-medium transition sm:px-4 sm:py-2 ${
                          activeTab === 'behaviour'
                            ? 'bg-brand text-white shadow-sm'
                            : 'text-gray-700 hover:bg-white'
                        }`}
                      >
                        Behaviour Tracking
                      </button>
                    ) : null}
                  </div>
                ) : null}

                {activeTab === 'details' || !showTabBar ? (
                  <>
                    <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {[
                        { label: 'Department', value: employee.department },
                        { label: 'Reporting Supervisor', value: employee.manager },
                        {
                          label: 'Is Reporting Supervisor',
                          value: employee.isReportingSupervisor,
                        },
                        {
                          label: 'Employment Nature',
                          value: employee.isOutsource,
                        },
                        {
                          label: 'Cost Centre Type',
                          value: employee.isProduction,
                        },
                        ...(!isOutsourceView
                          ? [{ label: 'Level Type', value: employee.levelType }]
                          : []),
                        { label: 'Date of Joining', value: employee.doj },
                        { label: 'Created At', value: employee.createdAt },
                        { label: 'Location', value: employee.location },
                      ].map((field) => (
                        <div
                          key={field.label}
                          className={`rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5 ${
                            field.wide ? 'sm:col-span-2' : ''
                          }`}
                        >
                          <p className="text-xs text-gray-500">{field.label}</p>
                          <p
                            className={`mt-0.5 text-sm font-medium text-gray-900 ${
                              field.wide ? 'break-all' : ''
                            }`}
                          >
                            {field.value}
                          </p>
                        </div>
                      ))}
                      <ProbationInfoFields
                        variant="details"
                        onProbation={employee.onProbation}
                        completionDate={employee.completionDate}
                      />
                      {[
                        { label: 'Date of Birth', value: employee.dob },
                        { label: 'Contact Number', value: employee.contact },
                        { label: 'Email ID', value: employee.email, wide: true },
                        { label: 'Aadhar Number', value: employee.aadharNo },
                        { label: 'PAN Number', value: employee.panNo },
                      ].map((field) => (
                        <div
                          key={field.label}
                          className={`rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5 ${
                            field.wide ? 'sm:col-span-2' : ''
                          }`}
                        >
                          <p className="text-xs text-gray-500">{field.label}</p>
                          <p
                            className={`mt-0.5 text-sm font-medium text-gray-900 ${
                              field.wide ? 'break-all' : ''
                            }`}
                          >
                            {field.value}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:max-w-2xl">
                      {[
                        { label: 'Aadhar', src: employee.aadharFrontImage },
                        { label: 'PAN', src: employee.panImage },
                      ].map((doc) => {
                        const src = resolveAssetUrl(doc.src) || doc.src;
                        const isPdf = Boolean(src) && isPdfUrl(src);
                        return (
                          <div key={doc.label} className="min-w-0">
                            <p className="mb-1 text-xs font-medium text-gray-500">{doc.label}</p>
                            <div className="aspect-[16/10] overflow-hidden rounded-lg border border-gray-100 bg-gray-100">
                              {!src ? (
                                <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                                  Not uploaded
                                </div>
                              ) : isPdf ? (
                                <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-[#faf8f5] px-3 text-center">
                                  <svg
                                    width="36"
                                    height="36"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.75"
                                    className="text-brand"
                                    aria-hidden
                                  >
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                    <line x1="8" y1="13" x2="16" y2="13" />
                                    <line x1="8" y1="17" x2="16" y2="17" />
                                    <line x1="8" y1="9" x2="10" y2="9" />
                                  </svg>
                                  <p className="line-clamp-2 text-xs font-medium text-gray-700">
                                    {fileNameFromUrl(src, doc.label)}
                                  </p>
                                  <a
                                    href={src}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="inline-flex items-center justify-center rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white shadow-sm"
                                  >
                                    View PDF
                                  </a>
                                </div>
                              ) : (
                                <img
                                  src={src}
                                  alt={doc.label}
                                  className="h-full w-full cursor-zoom-in object-contain"
                                  onClick={() =>
                                    setZoomImage({
                                      src,
                                      title: `${doc.label} - ${employee.name}`,
                                    })
                                  }
                                />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                  </>
                ) : null}

                {activeTab === 'leave' && showLeaveFeatures ? (
                  <div className="mt-6 space-y-4">
                    {!isOutsourceView ? (
                      <LeaveBalanceSummary
                        summary={leaveSummary}
                        isLoading={isLoadingLeaves && !leavesRes}
                      />
                    ) : null}

                    <div className="rounded-xl border border-gray-100 bg-[#faf8f5] p-4 sm:p-6">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">Leave History</h3>
                        {!isOutsourceView ? (
                          <p className="mt-1 hidden text-xs text-gray-600 sm:block">
                            Default showing current month. Use date range filter for custom history.
                          </p>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        {!isOutsourceView ? (
                          <FilterToggleButton
                            active={hasActiveLeaveFilters}
                            onClick={() => setIsFilterOpen((prev) => !prev)}
                            ariaLabel="Toggle leave filters"
                          />
                        ) : null}
                        {canApplyLeaveHere ? (
                          <button
                            type="button"
                            onClick={() => setIsApplyModalOpen(true)}
                            className="inline-flex items-center justify-center rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm"
                          >
                            Apply Leave
                          </button>
                        ) : null}
                      </div>
                    </div>

                    {!isOutsourceView && isFilterOpen ? (
                      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                        <label className="flex flex-col text-sm text-gray-700">
                          Status
                          <select
                            value={statusDraft}
                            onChange={(e) => {
                              setStatusDraft(e.target.value || DEFAULT_LEAVE_LIST_STATUS_FILTER);
                            }}
                            className="mt-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                          >
                            {LEAVE_STATUS_FILTER_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="flex flex-col text-sm text-gray-700">
                          From
                          <input
                            type="date"
                            value={dateDraft.from}
                            onChange={(e) => {
                              setDateDraft((prev) => ({ ...prev, from: e.target.value }));
                            }}
                            className="mt-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                          />
                        </label>
                        <label className="flex flex-col text-sm text-gray-700">
                          To
                          <input
                            type="date"
                            value={dateDraft.to}
                            onChange={(e) => {
                              setDateDraft((prev) => ({ ...prev, to: e.target.value }));
                            }}
                            className="mt-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                          />
                        </label>
                        <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-2">
                          <button
                            type="button"
                            onClick={() => {
                              setStatusFilter(statusDraft);
                              setDateFilter(dateDraft);
                              setPage(1);
                            }}
                            className="inline-flex w-full items-center justify-center rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white shadow-sm"
                          >
                            Search
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const nextStatus = DEFAULT_LEAVE_LIST_STATUS_FILTER;
                              const nextDates = {
                                from: defaultRange.start,
                                to: defaultRange.end,
                              };
                              setStatusDraft(nextStatus);
                              setStatusFilter(nextStatus);
                              setDateDraft(nextDates);
                              setDateFilter(nextDates);
                              setPage(1);
                            }}
                            className="inline-flex w-full items-center justify-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                    ) : null}

                    <ApiLoadState
                      isLoading={isLoadingLeaves}
                      isFetching={isFetchingLeaves}
                      hasData={hasLeaveData}
                      error={leavesError}
                      refetch={refetchLeaves}
                      message="Loading leaves..."
                      className="py-6 md:hidden"
                    />
                    {!leavesError && leaveListReady && pageItems.length === 0 ? (
                      <p className="py-6 text-center text-sm text-gray-500 md:hidden">
                        No leave records found.
                      </p>
                    ) : !leavesError && leaveListReady && pageItems.length > 0 ? (
                      <ul className="mb-4 mt-5 space-y-3 md:hidden">
                        {pageItems.map((leave, index) => {
                          const status = String(leave.status || 'pending');
                          const isFinal = /approved|accepted|extended|rejected|cancel/i.test(status);
                          const dateLabel = getLeaveDateLabel(leave);
                          return (
                            <li
                              key={leave.id}
                              className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm"
                            >
                              <div className="min-w-0">
                                <p className="text-xs text-gray-500">
                                  #{(currentPage - 1) * pageSize + index + 1} · {dateLabel}
                                </p>
                                <div className="mt-2 space-y-1 text-xs text-gray-600">
                                  <p>
                                    Leave Type:{' '}
                                    <span className="font-medium text-gray-800">
                                      {leave.leave_type || '-'}
                                    </span>
                                  </p>
                                  <p>
                                    Day Type:{' '}
                                    <span className="font-medium text-gray-800">
                                      {formatDayTypeLabel(leave.day_type)}
                                    </span>
                                  </p>
                                  <p>
                                    Description:{' '}
                                    <span className="font-medium text-gray-800">
                                      {leave.description || '-'}
                                    </span>
                                  </p>
                                  <p>
                                    Applied By:{' '}
                                    <span className="font-medium text-gray-800">
                                      {leave.apply_by || '-'}
                                    </span>
                                  </p>
                                  <p>
                                    Applied On:{' '}
                                    <span className="font-medium text-gray-800">
                                      {formatAppliedOn(leave.created_at)}
                                    </span>
                                  </p>
                                  <p>
                                    Status: <LeaveStatusBadge status={status} />
                                  </p>
                                </div>
                              </div>
                              <div className="mt-3 border-t border-gray-100 pt-3">
                                {renderLeaveActions(leave, { isFinal })}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}

                    <div className="mt-5 hidden overflow-x-auto md:block">
                      <table className="min-w-full border-collapse text-sm">
                        <thead>
                          <tr>
                            <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-700">
                              Applied On
                            </th>
                            <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-700">
                              Leave Date
                            </th>
                            <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-700">
                              Leave Type
                            </th>
                            <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-700">
                              Day Type
                            </th>
                            <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-700">
                              Description
                            </th>
                            <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-700">
                              Applied By
                            </th>
                            <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-700">
                              Status
                            </th>
                            <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-700">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <ApiLoadTableRow
                            colSpan={8}
                            isLoading={isLoadingLeaves}
                            isFetching={isFetchingLeaves}
                            hasData={hasLeaveData}
                            error={leavesError}
                            refetch={refetchLeaves}
                            message="Loading leaves..."
                          />
                          {!leavesError && leaveListReady && pageItems.length === 0 ? (
                            <tr>
                              <td colSpan={8} className="px-2 py-5 text-center text-gray-500">
                                No leave records found.
                              </td>
                            </tr>
                          ) : !leavesError && leaveListReady &&
                            pageItems.map((leave) => {
                              const status = String(leave.status || 'pending');
                              const isFinal = /approved|accepted|extended|rejected|cancel/i.test(status);
                              const dateLabel = getLeaveDateLabel(leave);
                              return (
                                <tr key={leave.id}>
                                  <td className="border-b border-gray-100 px-2 py-2 text-gray-800">
                                    {formatAppliedOn(leave.created_at)}
                                  </td>
                                  <td className="border-b border-gray-100 px-2 py-2 text-gray-800">
                                    {dateLabel}
                                  </td>
                                  <td className="border-b border-gray-100 px-2 py-2 text-gray-800">
                                    {leave.leave_type || '-'}
                                  </td>
                                  <td className="border-b border-gray-100 px-2 py-2 text-gray-800">
                                    {formatDayTypeLabel(leave.day_type)}
                                  </td>
                                  <td className="border-b border-gray-100 px-2 py-2 text-gray-800">
                                    {leave.description || '-'}
                                  </td>
                                  <td className="border-b border-gray-100 px-2 py-2 text-gray-800">
                                    {leave.apply_by || '-'}
                                  </td>
                                  <td className="border-b border-gray-100 px-2 py-2 text-gray-800">
                                    <LeaveStatusBadge status={status} />
                                  </td>
                                  <td className="border-b border-gray-100 px-2 py-2 whitespace-nowrap text-gray-800">
                                    {renderLeaveActions(
                                      leave,
                                      { isFinal },
                                      'flex flex-nowrap items-center gap-2'
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>

                    <TablePagination
                      totalCount={totalCount}
                      currentPage={currentPage}
                      onPageChange={setPage}
                      pageSize={effectivePageSize}
                    />
                    </div>
                  </div>
                ) : null}

                {activeTab === 'behaviour' && showBehaviourTab ? (
                  <div className="mt-6 space-y-4">
                    <div className="rounded-xl border border-gray-100 bg-[#faf8f5] p-4 sm:p-6">
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div className="relative w-full min-w-0 md:max-w-xs">
                          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <circle cx="11" cy="11" r="8" />
                              <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                          </span>
                          <input
                            type="text"
                            placeholder="Search Behaviour Tracking"
                            value={behaviourSearch}
                            onChange={(e) => {
                              setBehaviourSearch(e.target.value);
                              setBehaviourPage(1);
                            }}
                            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                          />
                        </div>
                        <div className="flex shrink-0 flex-wrap items-center gap-2">
                          <FilterToggleButton
                            active={hasBehaviourDateFilter}
                            onClick={() => setIsBehaviourFilterOpen((prev) => !prev)}
                            ariaLabel="Toggle date filter"
                          />
                          <button
                            type="button"
                            onClick={() => setIsBehaviourPerformanceOpen(true)}
                            className="inline-flex items-center justify-center rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm"
                          >
                            Performance
                          </button>
                        </div>
                      </div>

                      {isBehaviourFilterOpen ? (
                        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                          <label className="flex flex-col text-sm text-gray-700">
                            From
                            <input
                              type="date"
                              value={behaviourDateDraft.from}
                              onChange={(e) => {
                                setBehaviourDateDraft((prev) => ({
                                  ...prev,
                                  from: e.target.value,
                                }));
                              }}
                              className="mt-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                            />
                          </label>
                          <label className="flex flex-col text-sm text-gray-700">
                            To
                            <input
                              type="date"
                              value={behaviourDateDraft.to}
                              onChange={(e) => {
                                setBehaviourDateDraft((prev) => ({
                                  ...prev,
                                  to: e.target.value,
                                }));
                              }}
                              className="mt-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                            />
                          </label>
                          <div className="flex items-end gap-2 sm:col-span-2">
                            <button
                              type="button"
                              onClick={() => {
                                setBehaviourDateFilter(behaviourDateDraft);
                                setBehaviourPage(1);
                              }}
                              className="inline-flex w-full items-center justify-center rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white shadow-sm"
                            >
                              Search
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const nextDates = {
                                  from: defaultRange.start,
                                  to: defaultRange.end,
                                };
                                setBehaviourDateDraft(nextDates);
                                setBehaviourDateFilter(nextDates);
                                setBehaviourPage(1);
                                setIsBehaviourFilterOpen(false);
                              }}
                              className="inline-flex w-full items-center justify-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                              Reset
                            </button>
                          </div>
                        </div>
                      ) : null}

                      <ApiLoadState
                        isLoading={isLoadingBehaviour}
                        isFetching={isFetchingBehaviour}
                        hasData={filteredBehaviourRows.length > 0}
                        error={behaviourError}
                        refetch={refetchBehaviour}
                        message="Loading behaviour tracking…"
                        className="py-6 md:hidden"
                      />

                      {!behaviourError &&
                      !isLoadingBehaviour &&
                      !isFetchingBehaviour &&
                      behaviourPageItems.length === 0 ? (
                        <p className="py-6 text-center text-sm text-gray-500">
                          No behaviour tracking found.
                        </p>
                      ) : null}

                      {behaviourPageItems.length > 0 ? (
                        <>
                          <ul className="mt-4 space-y-3 md:hidden">
                            {behaviourPageItems.map((row, index) => (
                              <li
                                key={row.__key}
                                className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm"
                              >
                                <p className="text-xs text-gray-500">
                                  #{(behaviourCurrentPage - 1) * pageSize + index + 1}
                                </p>
                                <p className="mt-1 text-sm text-gray-700">
                                  Tracking Date:{' '}
                                  <span className="font-medium text-gray-900">
                                    {row.trackingDateLabel}
                                  </span>
                                </p>
                                <p className="text-sm text-gray-700">
                                  Review Month:{' '}
                                  <span className="font-medium text-gray-900">
                                    {row.reviewMonth}
                                  </span>
                                </p>
                                <p className="text-sm text-gray-700">
                                  Reporting Manager:{' '}
                                  <span className="font-medium text-gray-900">
                                    {row.reportingManager || '—'}
                                  </span>
                                </p>
                                <button
                                  type="button"
                                  onClick={() => setViewBehaviour(row)}
                                  className="mt-3 inline-flex rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white"
                                >
                                  Behaviour Review
                                </button>
                              </li>
                            ))}
                          </ul>

                          <div className="mt-4 hidden overflow-x-auto md:block">
                            <table className="min-w-full border-collapse text-sm">
                              <thead>
                                <tr>
                                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                                    Sr. No.
                                  </th>
                                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                                    Tracking Date
                                  </th>
                                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                                    Review Month
                                  </th>
                                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                                    Reporting Manager
                                  </th>
                                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                                    Behaviour
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                <ApiLoadTableRow
                                  colSpan={5}
                                  isLoading={isLoadingBehaviour}
                                  isFetching={isFetchingBehaviour}
                                  hasData={filteredBehaviourRows.length > 0}
                                  error={behaviourError}
                                  refetch={refetchBehaviour}
                                  message="Loading behaviour tracking..."
                                />
                                {behaviourPageItems.map((row, index) => (
                                  <tr key={row.__key} className="hover:bg-white/70">
                                    <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                                      {(behaviourCurrentPage - 1) * pageSize + index + 1}
                                    </td>
                                    <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                                      {row.trackingDateLabel}
                                    </td>
                                    <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                                      {row.reviewMonth}
                                    </td>
                                    <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                                      {row.reportingManager || '—'}
                                    </td>
                                    <td className="border-b border-gray-100 px-3 py-2.5">
                                      <button
                                        type="button"
                                        onClick={() => setViewBehaviour(row)}
                                        className="inline-flex rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90"
                                      >
                                        Behaviour Review
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          <TablePagination
                            totalCount={behaviourTotalCount}
                            currentPage={behaviourCurrentPage}
                            onPageChange={setBehaviourPage}
                            pageSize={pageSize}
                          />
                        </>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      </div>

      <BehaviourPerformanceModal
        open={isBehaviourPerformanceOpen}
        performance={behaviourPerformance}
        onClose={() => setIsBehaviourPerformanceOpen(false)}
      />

      {viewBehaviour ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4"
          onClick={() => setViewBehaviour(null)}
          role="presentation"
        >
          <div
            className="flex max-h-[min(90vh,720px)] w-full max-w-2xl flex-col rounded-2xl bg-white px-5 py-4 shadow-2xl sm:px-6 sm:py-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex shrink-0 items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800 sm:text-xl">Behaviour Review</h3>
              <button
                type="button"
                onClick={() => setViewBehaviour(null)}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-2xl leading-none text-brand hover:bg-gray-100"
                aria-label="Close behaviour review"
              >
                ×
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2">
                  <p className="text-xs text-gray-500">Tracking Date</p>
                  <p className="mt-0.5 text-sm font-medium text-gray-900">
                    {viewBehaviour.trackingDateLabel || '—'}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2">
                  <p className="text-xs text-gray-500">Reviewed By</p>
                  <p className="mt-0.5 text-sm font-medium text-gray-900">
                    {viewBehaviour.reviewedBy || '—'}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2">
                  <p className="text-xs text-gray-500">Compliance Rate</p>
                  <p className="mt-0.5 text-sm font-medium text-gray-900">
                    {formatPercent(
                      viewBehaviour.monthlyPerformance ??
                        viewBehaviour.complianceSummary?.complianceRate
                    )}
                  </p>
                </div>
              </div>
              {viewBehaviour.behaviours.length ? (
                <div className="overflow-x-auto rounded-lg border border-gray-100">
                  <table className="min-w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-[#faf8f5]">
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Code</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">
                          Occurrences
                        </th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewBehaviour.behaviours.map((item, idx) => (
                        <tr key={item.id ?? `${item.behaviour_id ?? item.behaviour_name}-${idx}`}>
                          <td className="border-t border-gray-100 px-3 py-2 text-gray-800">
                            {firstBehaviourText(item.behaviour_name, item.code, item.parameter) ||
                              '—'}
                          </td>
                          <td className="border-t border-gray-100 px-3 py-2 text-gray-800">
                            {formatComplianceStatus(item)}
                          </td>
                          <td className="border-t border-gray-100 px-3 py-2 text-gray-800">
                            {getNonComplianceCount(item) ?? '—'}
                          </td>
                          <td className="border-t border-gray-100 px-3 py-2 text-gray-800">
                            {firstBehaviourText(
                              item.manager_remarks,
                              item.remarks,
                              item.remark
                            ) || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="py-6 text-center text-sm text-gray-500">
                  No behaviour details found.
                </p>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {viewLeave ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => {
            setIsLeaveHistoryOpen(false);
            setViewLeave(null);
          }}
          role="presentation"
        >
          <div
            className="flex max-h-[90vh] w-full max-w-xl flex-col rounded-2xl bg-white px-6 py-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex shrink-0 items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-800">Leave Details</h3>
              <button
                type="button"
                onClick={() => {
                  setIsLeaveHistoryOpen(false);
                  setViewLeave(null);
                }}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-2xl leading-none text-brand hover:bg-gray-100"
                aria-label="Close leave details"
              >
                ×
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="grid gap-3 sm:grid-cols-2">
                {viewLeave.account_name || viewLeave.account?.name ? (
                  <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5">
                    <p className="text-xs text-gray-500">Employee</p>
                    <p className="mt-0.5 text-sm font-medium text-gray-900">
                      {viewLeave.account_name || viewLeave.account?.name}
                      {viewLeave.account?.employee_id
                        ? ` (${viewLeave.account.employee_id})`
                        : ''}
                    </p>
                  </div>
                ) : null}
                <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5">
                  <p className="text-xs text-gray-500">Leave Date</p>
                  <p className="mt-0.5 text-sm font-medium text-gray-900">
                    {getLeaveDateLabel(viewLeave)}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5">
                  <p className="text-xs text-gray-500">Leave Type</p>
                  <p className="mt-0.5 text-sm font-medium text-gray-900">
                    {viewLeave.leave_type || '-'}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5">
                  <p className="text-xs text-gray-500">Day Type</p>
                  <p className="mt-0.5 text-sm font-medium text-gray-900">
                    {formatDayTypeLabel(viewLeave.day_type)}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5">
                  <p className="text-xs text-gray-500">Applied By</p>
                  <p className="mt-0.5 text-sm font-medium text-gray-900">
                    {viewLeave.apply_by ||
                      formatHistoryActor(viewLeave.applied_by) ||
                      '-'}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5">
                  <p className="text-xs text-gray-500">Applied On</p>
                  <p className="mt-0.5 text-sm font-medium text-gray-900">
                    {formatAppliedOn(viewLeave.created_at)}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5">
                  <p className="text-xs text-gray-500">Status</p>
                  <LeaveStatusBadge status={viewLeave.status} className="mt-0.5" />
                </div>
                <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5 sm:col-span-2">
                  <p className="text-xs text-gray-500">
                    {isOutsourceView ? 'Comment' : 'Description'}
                  </p>
                  <p className="mt-0.5 text-sm font-medium text-gray-900">
                    {viewLeave.description || viewLeave.comment || '-'}
                  </p>
                </div>
              </div>

              {isOutsourceView ? (
                <div className="mt-5 border-t border-gray-100 pt-4">
                  {(() => {
                    const histories = (
                      Array.isArray(viewLeave.histories) ? viewLeave.histories : []
                    )
                      .slice()
                      .sort((a, b) => {
                        const ta = new Date(a?.created_at || 0).getTime();
                        const tb = new Date(b?.created_at || 0).getTime();
                        return ta - tb;
                      });
                    const historyCount = histories.length;
                    return (
                      <>
                        <button
                          type="button"
                          onClick={() => setIsLeaveHistoryOpen((prev) => !prev)}
                          className={`group flex w-full items-center justify-between gap-3 rounded-xl border px-3.5 py-3 text-left transition ${
                            isLeaveHistoryOpen
                              ? 'border-brand/25 bg-brand/5 shadow-sm'
                              : 'border-gray-200 bg-[#faf8f5] hover:border-brand/20 hover:bg-white'
                          }`}
                          aria-expanded={isLeaveHistoryOpen}
                        >
                          <span className="flex min-w-0 items-center gap-3">
                            <span
                              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                                isLeaveHistoryOpen
                                  ? 'bg-brand text-white'
                                  : 'bg-white text-brand ring-1 ring-brand/15'
                              }`}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                className="h-[18px] w-[18px]"
                                aria-hidden
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M12 8v4l2.5 1.5M21 12a9 9 0 11-3.16-6.75"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M21 5v4h-4"
                                />
                              </svg>
                            </span>
                            <span className="min-w-0">
                              <span className="block text-sm font-semibold text-gray-900">
                                Activity History
                              </span>
                              <span className="mt-0.5 block text-xs text-gray-500">
                                {historyCount > 0
                                  ? `${historyCount} update${historyCount === 1 ? '' : 's'}`
                                  : 'No updates yet'}
                              </span>
                            </span>
                          </span>
                          <span
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition ${
                              isLeaveHistoryOpen
                                ? 'bg-brand/10 text-brand'
                                : 'bg-white text-gray-500 ring-1 ring-gray-200 group-hover:text-brand'
                            }`}
                            aria-hidden
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              className={`h-4 w-4 transition-transform duration-200 ${
                                isLeaveHistoryOpen ? 'rotate-180' : ''
                              }`}
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </span>
                        </button>

                        {isLeaveHistoryOpen ? (
                          <div className="mt-3">
                            {!historyCount ? (
                              <p className="text-sm text-gray-500">No history yet.</p>
                            ) : (
                              <ul className="space-y-0">
                                {histories.map((item, index) => {
                                  const actor = formatHistoryActor(item?.action_by);
                                  const endLine = formatHistoryEndDateLine(
                                    item?.old_end_date,
                                    item?.new_end_date
                                  );
                                  const comment = String(item?.comment || '').trim();
                                  const isLast = index === histories.length - 1;
                                  return (
                                    <li
                                      key={
                                        item?.id ??
                                        `${item?.action_type}-${item?.created_at}-${index}`
                                      }
                                      className="relative flex gap-3 pb-4 last:pb-0"
                                    >
                                      <div className="relative flex w-4 shrink-0 flex-col items-center">
                                        <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-brand ring-4 ring-brand/15" />
                                        {!isLast ? (
                                          <span className="mt-1 w-px flex-1 bg-gray-200" />
                                        ) : null}
                                      </div>
                                      <div className="min-w-0 flex-1 rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                          <LeaveStatusBadge
                                            status={item?.action_type}
                                            className="shrink-0"
                                          />
                                          <span className="text-xs text-gray-500">
                                            {formatAppliedOn(item?.created_at)}
                                          </span>
                                        </div>
                                        {actor ? (
                                          <p className="mt-1.5 text-sm font-medium text-gray-900">
                                            {actor}
                                          </p>
                                        ) : (
                                          <p className="mt-1.5 text-sm font-medium text-gray-900">
                                            {formatHistoryActionLabel(item?.action_type)}
                                          </p>
                                        )}
                                        {endLine ? (
                                          <p className="mt-1 text-xs text-gray-600">
                                            {endLine}
                                          </p>
                                        ) : null}
                                        {comment ? (
                                          <p className="mt-1 text-sm text-gray-600">
                                            “{comment}”
                                          </p>
                                        ) : null}
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        ) : null}
                      </>
                    );
                  })()}
                </div>
              ) : null}
            </div>
            <div className="mt-5 flex shrink-0 justify-end">
              <button
                type="button"
                onClick={() => {
                  setIsLeaveHistoryOpen(false);
                  setViewLeave(null);
                }}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isApplyModalOpen && canApplyLeaveHere ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setIsApplyModalOpen(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-2xl rounded-2xl bg-white px-6 py-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-800">Apply Leave</h3>
              <button
                type="button"
                onClick={() => setIsApplyModalOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-2xl leading-none text-brand hover:bg-gray-100"
                aria-label="Close apply leave modal"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateLeave} className="grid gap-4 md:grid-cols-2">
              <label className="flex flex-col text-sm text-gray-700">
                Leave Duration
                <select
                  value={leaveForm.leave_duration}
                  onChange={handleLeaveChange('leave_duration')}
                  className={formInputClasses}
                >
                  {LEAVE_DURATION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              {leaveForm.leave_duration === 'multiple' ? (
                <>
                  <label className="flex flex-col text-sm text-gray-700">
                    Start Date
                    <input
                      type="date"
                      value={leaveForm.start_date}
                      onChange={handleLeaveChange('start_date')}
                      className={formInputClasses}
                    />
                  </label>
                  <label className="flex flex-col text-sm text-gray-700">
                    End Date
                    <input
                      type="date"
                      value={leaveForm.end_date}
                      onChange={handleLeaveChange('end_date')}
                      min={leaveForm.start_date || undefined}
                      className={formInputClasses}
                    />
                  </label>
                </>
              ) : (
                <label className="flex flex-col text-sm text-gray-700">
                  Leave Date
                  <input
                    type="date"
                    value={leaveForm.start_date}
                    onChange={handleLeaveChange('start_date')}
                    className={formInputClasses}
                  />
                </label>
              )}

              {!isOutsourceView ? (
              <label className="flex flex-col text-sm text-gray-700">
                Leave Type
                <select
                  value={leaveForm.leave_type}
                  onChange={handleLeaveChange('leave_type')}
                  className={formInputClasses}
                >
                  {leaveTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              ) : null}

              <label className="flex flex-col text-sm text-gray-700">
                Day Type
                <select
                  value={leaveForm.day_type}
                  onChange={handleLeaveChange('day_type')}
                  className={formInputClasses}
                >
                  {DAY_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col text-sm text-gray-700">
                Apply By
                <input
                  type="text"
                  value={applyBy}
                  readOnly
                  className={formReadonlyClasses}
                />
              </label>

              <label className="flex flex-col text-sm text-gray-700 md:col-span-2">
                {isOutsourceView ? 'Comment' : 'Description'}
                <textarea
                  rows={3}
                  value={leaveForm.description}
                  onChange={handleLeaveChange('description')}
                  placeholder="Personal work"
                  className={formInputClasses}
                />
              </label>

              <div className="flex justify-end gap-3 md:col-span-2">
                <button
                  type="button"
                  onClick={() => setIsApplyModalOpen(false)}
                  className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreatingLeave}
                  className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {isCreatingLeave ? 'Applying...' : 'Apply Leave'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {statusAction && isOutsourceView ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setStatusAction(null)}
          role="presentation"
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white px-6 py-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-800">
                {statusAction.status === 'approved'
                  ? 'Approve Leave'
                  : statusAction.status === 'rejected'
                    ? 'Reject Leave'
                    : 'Cancel Leave'}
              </h3>
              <button
                type="button"
                onClick={() => setStatusAction(null)}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-2xl leading-none text-brand hover:bg-gray-100"
                aria-label="Close leave status modal"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleOutsourceLeaveStatus} className="grid gap-4">
              <label className="flex flex-col text-sm text-gray-700">
                Comment
                <textarea
                  rows={3}
                  value={statusComment}
                  onChange={(e) => setStatusComment(e.target.value)}
                  placeholder="Enter comment"
                  className={formInputClasses}
                />
              </label>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setStatusAction(null)}
                  className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  type="submit"
                  disabled={isUpdatingOutsourceStatus}
                  className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {isUpdatingOutsourceStatus ? 'Updating...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {extendLeave && isOutsourceView ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setExtendLeave(null)}
          role="presentation"
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white px-6 py-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-800">Extend Leave</h3>
              <button
                type="button"
                onClick={() => setExtendLeave(null)}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-2xl leading-none text-brand hover:bg-gray-100"
                aria-label="Close extend leave modal"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleExtendLeave} className="grid gap-4">
              <label className="flex flex-col text-sm text-gray-700">
                End Date
                <input
                  type="date"
                  value={extendForm.end_date}
                  min={extendLeave.start_date || undefined}
                  onChange={(e) =>
                    setExtendForm((prev) => ({ ...prev, end_date: e.target.value }))
                  }
                  className={formInputClasses}
                />
              </label>
              <label className="flex flex-col text-sm text-gray-700">
                Comment
                <textarea
                  rows={3}
                  value={extendForm.comment}
                  onChange={(e) =>
                    setExtendForm((prev) => ({ ...prev, comment: e.target.value }))
                  }
                  className={formInputClasses}
                />
              </label>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setExtendLeave(null)}
                  className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUpdatingOutsourceLeave}
                  className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {isUpdatingOutsourceLeave ? 'Updating...' : 'Update Leave'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {zoomImage ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setZoomImage(null)}
          role="presentation"
        >
          <div className="relative max-h-[90vh] max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setZoomImage(null)}
              className="absolute -right-3 -top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white text-xl leading-none text-gray-800 shadow-lg hover:bg-gray-100"
              aria-label="Close preview"
            >
              ×
            </button>
            <img
              src={zoomImage.src}
              alt={zoomImage.title || 'Preview'}
              className="max-h-[90vh] w-full rounded-lg object-contain"
            />
            {zoomImage.title ? (
              <p className="mt-2 text-center text-sm font-medium text-white">{zoomImage.title}</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </DashboardLayout>
  );
}

export default AdminEmployeeDetailsPage;
